import React, { Fragment, PureComponent } from 'react';

import { injectIntl, intlShape } from 'react-intl';
import {
  GoogleMap,
  Marker,
  Polygon,
  Polyline,
  TrafficLayer,
  withGoogleMap,
} from 'react-google-maps';
import { MAP } from 'react-google-maps/lib/constants';
import { arrayOf, bool, func, number, object, oneOfType, shape } from 'prop-types';
import { FormControlLabel } from '@mui/material';
import { Switch } from '@mui/material';
import { createPortal } from 'react-dom';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { debounce } from 'lodash';
import { logDifferencesIfAny } from 'util/DeepDiffUtil';

import { Button } from 'components/Button';
import { MARKER_TYPE } from 'constants/Values';

import {
  ArrowUtility,
  clusterLocationHistoryMarkers,
  stationStopOrderSort,
} from 'util/RouteDetailsUtil';
import { VISITED } from 'constants/States';

import { DEFAULT_ZOOM_LEVEL, mapStyles } from './helpers';
import {
  SCHOOL_ICON_DATA_URI,
  SCHOOL_LABEL_GAP_PX,
  SCHOOL_MARKER_PX,
  schoolLocations,
} from './schoolLocations';
import MapMarker from './MapMarker';
import markerShape from '../../constants/propTypes/markerShape';

import styles from './styles.module.scss';

class PopUpInfoWindowGoogleMap extends PureComponent {
  static propTypes = {
    markers: arrayOf(markerShape),
    center: shape({
      lat: oneOfType([number, func]),
      lng: oneOfType([number, func]),
    }).isRequired,
    zoom: number,
    bounds: object, // It's from Google maps and it's impossible to predict
    onBoundsChanged: func.isRequired,
    onMapMounted: func.isRequired,
    onSearchBoxMounted: func.isRequired,
    onSearchBoxInputMounted: func.isRequired,
    onMapClick: func.isRequired,
    onPlacesChanged: func.isRequired,
    onMarkerDragEnd: func.isRequired,
    onMarkerClick: func.isRequired,
    onMarkerHover: func.isRequired,
    onRecenterClick: func,
    withSearchBox: bool,
    showRecenterButton: bool,
    enableTrafficToggle: bool,
    enableArrowsToggle: bool,
    enableLocationHistoryToggle: bool,
    markersDragAllowed: bool,
    defaultMarkersView: bool,
    intl: intlShape.isRequired,
    onUserInteraction: func,
    onCenterChanged: func,
    onZoomChanged: func,
    onLocationHistoryToggleUpdate: func,
    onTrafficToggleUpdate: func,
    onArrowsToggleUpdate: func,
    busLocationHistoryEnabled: bool,
    trafficVisible: bool,
    arrowsVisible: bool,
  };

  static defaultProps = {
    markers: [],
    defaultMarkersView: false,
    markersDragAllowed: false,
    bounds: null,
    withSearchBox: false,
    showRecenterButton: false,
    zoom: DEFAULT_ZOOM_LEVEL,
    enableTrafficToggle: false,
    enableArrowsToggle: false,
    enableLocationHistoryToggle: false,
    busLocationHistoryEnabled: false,
    trafficVisible: false,
    arrowsVisible: false,
    onUserInteraction: () => {},
    onRecenterClick: () => {},
    onCenterChanged: () => {},
    onZoomChanged: () => {},
    onLocationHistoryToggleUpdate: () => {},
    onTrafficToggleUpdate: () => {},
    onArrowsToggleUpdate: () => {},
    onMarkerHover: () => {},
  };
  static contextTypes = { [MAP]: object };
  state = {
    zOrders: {},
  };

  componentDidMount() {
    if (this.props.withSearchBox && this.context[MAP]) {
      const map = this.context[MAP];
      const input = this.searchBoxControlDiv.querySelector('input');
      if (input) {
        const searchBox = new google.maps.places.SearchBox(input);
        // Bias predictions to the currently visible region, matching the old wrapper's
        // `bounds` prop behaviour (kept in sync in componentDidUpdate).
        const initialBounds = this.props.bounds || map.getBounds();
        if (initialBounds) {
          searchBox.setBounds(initialBounds);
        }
        searchBox.addListener('places_changed', () => this.props.onPlacesChanged());
        map.controls[google.maps.ControlPosition.TOP_LEFT].push(this.searchBoxControlDiv);
        this.searchBox = searchBox;
        this.props.onSearchBoxMounted(searchBox);
      }
    }
    if (this.props.enableTrafficToggle && this.context[MAP]) {
      this.trafficControlDiv.style.marginTop = '6px';
      this.trafficControlDiv.style.marginLeft = '6px';
      const map = this.context[MAP];
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(this.trafficControlDiv);
    }
    if (this.props.enableArrowsToggle && this.context[MAP]) {
      this.arrowsControlDiv.style.marginTop = '6px';
      this.arrowsControlDiv.style.marginLeft = '6px';
      const map = this.context[MAP];
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(this.arrowsControlDiv);
    }
    if (this.props.enableLocationHistoryToggle && this.context[MAP]) {
      this.locationHistoryControlDiv.style.marginTop = '6px';
      this.locationHistoryControlDiv.style.marginLeft = '6px';
      const map = this.context[MAP];
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(this.locationHistoryControlDiv);
    }
    if (this.context[MAP]) {
      this.recenterControlDiv.style.marginBottom = '50px';
      this.recenterControlDiv.style.marginLeft = '0px';
      this.recenterControlDiv.style.minWidth = '200px';
      this.recenterControlDiv.style.display = 'flex';
      this.recenterControlDiv.style.justifyContent = 'space-evenly';

      const map = this.context[MAP];
      map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(this.recenterControlDiv);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.searchBox && this.props.bounds && prevProps.bounds !== this.props.bounds) {
      this.searchBox.setBounds(this.props.bounds);
    }
    logDifferencesIfAny(
      'PopUpInfoWindowGoogleMap.componentDidUpdate',
      {
        props: prevProps,
        state: prevState,
      },
      {
        props: this.props,
        state: this.state,
      },
      true,
    );
  }

  componentWillUnmount() {
    if (this.searchBox) {
      google.maps.event.clearInstanceListeners(this.searchBox);
      this.searchBox = null;
    }
  }

  onMouseDragStart = () => this.props.onUserInteraction({ type: 'drag' });
  onMouseDragEnd = () => {
    const center = this.context[MAP].getCenter();
    return this.props.onCenterChanged({
      lat: center.lat(),
      lng: center.lng(),
    });
  };
  onZoomChanged = debounce(() => this.props.onZoomChanged(this.context[MAP].getZoom()), 100);

  getDefaultZOrder = marker => (marker.type === MARKER_TYPE.BUS ? 1 : 2); // BUS has lower default

  getHighestZOrder = () => {
    const { zOrders } = this.state;
    return Math.max(0, ...Object.values(zOrders));
  };

  setTrafficVisible(value) {
    const { onTrafficToggleUpdate } = this.props;
    onTrafficToggleUpdate(value);
  }

  setArrowsVisible(value) {
    const { onArrowsToggleUpdate } = this.props;
    onArrowsToggleUpdate(value);
  }

  setLocationHistoryVisible(value) {
    const { onLocationHistoryToggleUpdate } = this.props;
    onLocationHistoryToggleUpdate(value);
  }

  getSortedArrowMarkers = markers => {
    if (this.props.busLocationHistoryEnabled) {
      const markerTypes = [MARKER_TYPE.LOCATION_HISTORY];
      return markers.filter(m => markerTypes.includes(m.type));
    }
    const markerTypes = [MARKER_TYPE.PLANNED_STATION, MARKER_TYPE.UNPLANNED_STATION];
    return markers
      .filter(m => markerTypes.includes(m.type) && m.state === VISITED)
      .sort(stationStopOrderSort);
  };

  getSortedStationMarkers = markers =>
    markers
      .filter(
        m =>
          [MARKER_TYPE.PLANNED_STATION, MARKER_TYPE.UNPLANNED_STATION].includes(m.type) &&
          m.state === VISITED,
      )
      .sort(stationStopOrderSort);

  handleInfoWindowClick = (markerId, markerType) => {
    this.setState(prevState => {
      const currentZOrder =
        prevState.zOrders[markerId] || this.getDefaultZOrder({ type: markerType });
      const highestZOrder = this.getHighestZOrder();
      return {
        zOrders: {
          ...prevState.zOrders,
          [markerId]: Math.max(currentZOrder, highestZOrder + 1), // Bring to front
        },
      };
    });
  };

  trafficControlDiv = document.createElement('div');
  arrowsControlDiv = document.createElement('div');
  recenterControlDiv = document.createElement('div');
  locationHistoryControlDiv = document.createElement('div');
  searchBoxControlDiv = document.createElement('div');

  renderMapMarkers = () => {
    const {
      markers,
      markersDragAllowed,
      onMarkerDragEnd,
      onMarkerClick,
      onMarkerHover,
      defaultMarkersView,
    } = this.props;
    const { zOrders } = this.state;
    const { busLocationHistoryEnabled } = this.props;

    const sortedStationMarkers = this.getSortedStationMarkers(markers);

    const firstMarkerId = sortedStationMarkers[0] && sortedStationMarkers[0].markerId;
    const lastMarkerId =
      sortedStationMarkers[sortedStationMarkers.length - 1] &&
      sortedStationMarkers[sortedStationMarkers.length - 1].markerId;

    return clusterLocationHistoryMarkers(markers, this.context[MAP]).map((marker, index) => {
      const requireRenderStation =
        marker.type === MARKER_TYPE.PLANNED_STATION ||
        (marker.type === MARKER_TYPE.UNPLANNED_STATION &&
          (marker.hasActiveDeviation ||
            marker.markerId === firstMarkerId ||
            marker.markerId === lastMarkerId));

      const zOrder = zOrders[marker.markerId] || this.getDefaultZOrder(marker);

      if (marker.type === MARKER_TYPE.BUS) {
        return (
          <MapMarker
            key={index}
            marker={marker}
            markersDragAllowed={markersDragAllowed}
            onMarkerDragEnd={onMarkerDragEnd}
            onMarkerClick={onMarkerClick}
            defaultMarkersView={defaultMarkersView}
            zOrder={zOrder}
            onInfoWindowClick={() => this.handleInfoWindowClick(marker.markerId, marker.type)}
          />
        );
      } else if (requireRenderStation) {
        return (
          <MapMarker
            key={index}
            marker={marker}
            markersDragAllowed={markersDragAllowed}
            onMarkerDragEnd={onMarkerDragEnd}
            onMarkerClick={onMarkerClick}
            defaultMarkersView={defaultMarkersView}
            zOrder={zOrder}
            enableInfoWindowAutoPan
            onInfoWindowClick={() => this.handleInfoWindowClick(marker.markerId, marker.type)}
          />
        );
      } else if (busLocationHistoryEnabled && marker.type === MARKER_TYPE.LOCATION_HISTORY) {
        return (
          <MapMarker
            key={index}
            marker={marker}
            markersDragAllowed={markersDragAllowed}
            onMarkerDragEnd={onMarkerDragEnd}
            onMarkerClick={onMarkerClick}
            defaultMarkersView={defaultMarkersView}
            zOrder={zOrder}
          />
        );
      } else if (
        marker.type === MARKER_TYPE.PUPIL_STOP_TEMPLATE ||
        marker.type === MARKER_TYPE.ROUTE_STOP_TEMPLATE
      ) {
        return (
          <MapMarker
            key={index}
            marker={marker}
            markersDragAllowed={false}
            onMarkerDragEnd={onMarkerDragEnd}
            onMarkerClick={onMarkerClick}
            onMarkerHover={onMarkerHover}
            defaultMarkersView={false}
            zOrder={zOrder}
            enableInfoWindowAutoPan
          />
        );
      }
      return false;
    });
  };

  // ── THE SCHOOL BADGES ───────────────────────────────────────────────────────
  //
  // Same campuses, same resolver and the same single icon asset as the V2 map
  // (`./schoolLocations.js`), so a school cannot end up drawn in two different places
  // on two screens of one console.
  //
  // Every screen that mounts this component gets them — Overview, the route history
  // map, Student Agenda, Add bus stop, the diversion shelter picker. Matt asked for
  // Overview and V2 explicitly and did not mind the rest; one render here is fewer
  // moving parts than a prop threaded through five call sites to withhold it.
  //
  // TWO THINGS IT DELIBERATELY IS NOT:
  //
  //  * NOT in the `markers` prop. `getMapCenter` / `getMapZoomLevel` (./helpers.js)
  //    derive the viewport from that array, so folding a campus in would pull the
  //    Overview map's fit away from the buses it exists to show whenever the two are
  //    far apart.
  //  * NOT clickable. Several of these screens place a stop on a map click, and a
  //    40px badge that swallowed one would break that on the tile it sits on.
  renderSchoolMarkers = () => {
    const locations = schoolLocations();
    if (!locations.length) return null;
    const half = SCHOOL_MARKER_PX / 2;
    return locations.map((location, index) => (
      <Marker
        key={`school-${index}`}
        position={{ lat: location.lat, lng: location.lng }}
        clickable={false}
        options={{
          // BELOW every other marker, which keeps a bus parked at the school from
          // disappearing under its badge.
          //
          // MEASURED rather than assumed (qa-tst, 2026-09-07), because the docs only
          // say markers without a `zIndex` are ordered "according to their vertical
          // position on screen" and do not say what that resolves to: Google honours
          // -1 literally (it does NOT clamp to 0), and an implicitly-ordered marker
          // resolves to a positive computed value — 986 on a 300px-tall probe map.
          // So a negative value is below every marker on these pages, all of which
          // pass no `zIndex` at all.
          zIndex: -1,
        }}
        icon={{
          url: SCHOOL_ICON_DATA_URI,
          scaledSize: new google.maps.Size(SCHOOL_MARKER_PX, SCHOOL_MARKER_PX),
          // Centred on the coordinate, matching the V2 overlay's own
          // translate(-50%,-50%) so the badge means the same thing on both surfaces.
          anchor: new google.maps.Point(half, half),
          // In ICON-IMAGE coordinates, so the same `size + gap` expression V2's
          // wrapper uses, from the same origin: a gap below the badge's bottom edge.
          labelOrigin: new google.maps.Point(half, SCHOOL_MARKER_PX + SCHOOL_LABEL_GAP_PX),
        }}
        label={
          location.name
            ? {
                text: location.name,
                fontSize: '11px',
                fontWeight: '700',
                color: '#1F2A44',
              }
            : undefined
        }
      />
    ));
  };

  renderMarkerArrows = () => {
    const { markers } = this.props;
    const sortedMarkers = clusterLocationHistoryMarkers(
      this.getSortedArrowMarkers(markers),
      this.context[MAP],
    );
    const arrows = ArrowUtility.calculateArrows(sortedMarkers, this.context[MAP]);
    const arrowColor = '#000000';
    const arrowOpacity = 1;

    return arrows.map((arrow, index) => (
      <Fragment key={index}>
        <Polyline
          path={arrow.shaft}
          options={{
            strokeColor: arrowColor,
            strokeWeight: 1,
            strokeOpacity: 0,
            icons: [
              {
                icon: {
                  path: 'M 0,-1.5 0,1.5',
                  strokeOpacity: arrowOpacity,
                  scale: 1.2,
                },
                offset: '0',
                repeat: '9px',
              },
            ],
          }}
        />
        <Polygon
          path={arrow.head}
          options={{
            fillOpacity: arrowOpacity,
            fillColor: arrowColor,
            strokeOpacity: 0,
            strokeWeight: 0,
          }}
        />
      </Fragment>
    ));
  };

  render() {
    const {
      bounds,
      onMapMounted,
      zoom,
      center,
      onMapClick,
      onBoundsChanged,
      withSearchBox,
      onSearchBoxInputMounted,
      intl,
      enableTrafficToggle,
      enableArrowsToggle,
      enableLocationHistoryToggle,
      showRecenterButton,
      onRecenterClick,
      busLocationHistoryEnabled,
      trafficVisible,
      arrowsVisible,
    } = this.props;

    return (
      <GoogleMap
        bounds={bounds}
        ref={withSearchBox ? onMapMounted : undefined}
        defaultZoom={DEFAULT_ZOOM_LEVEL}
        zoom={zoom}
        center={center}
        onClick={onMapClick}
        onBoundsChanged={withSearchBox ? onBoundsChanged : undefined}
        defaultOptions={{
          styles: mapStyles,
        }}
        onDragStart={this.onMouseDragStart}
        onDragEnd={this.onMouseDragEnd}
        onCenterChanged={this.onCenterChanged}
        onZoomChanged={this.onZoomChanged}
      >
        {trafficVisible && <TrafficLayer />}
        {withSearchBox &&
          createPortal(
            <input
              ref={onSearchBoxInputMounted}
              type="text"
              placeholder={intl.formatMessage({ id: 'map.search.placeholder' })}
              className={styles.searchBoxInput}
            />,
            this.searchBoxControlDiv,
          )}

        {this.renderSchoolMarkers()}
        {arrowsVisible && this.renderMarkerArrows()}
        {this.renderMapMarkers()}

        {enableTrafficToggle &&
          createPortal(
            <FormControlLabel
              control={
                <Switch
                  checked={trafficVisible}
                  onChange={(event, checked) => this.setTrafficVisible(checked)}
                  color="default"
                />
              }
              label={
                <span className={styles.controlLabel}>
                  {intl.formatMessage({ id: 'pages.routesOverview.trafficVisible' })}
                </span>
              }
            />,
            this.trafficControlDiv,
          )}
        {enableArrowsToggle &&
          createPortal(
            <FormControlLabel
              control={
                <Switch
                  checked={arrowsVisible}
                  onChange={(event, checked) => this.setArrowsVisible(checked)}
                  color="default"
                />
              }
              label={
                <span className={styles.controlLabel}>
                  {intl.formatMessage({ id: 'pages.routesOverview.arrowsVisible' })}
                </span>
              }
            />,
            this.arrowsControlDiv,
          )}
        {enableLocationHistoryToggle &&
          createPortal(
            <FormControlLabel
              control={
                <Switch
                  checked={busLocationHistoryEnabled}
                  onChange={(event, checked) => this.setLocationHistoryVisible(checked)}
                  color="default"
                />
              }
              label={
                <span className={styles.controlLabel}>
                  {intl.formatMessage({ id: 'pages.routesOverview.locationHistoryVisible' })}
                </span>
              }
            />,
            this.locationHistoryControlDiv,
          )}
        {showRecenterButton &&
          createPortal(
            <Button
              onClick={onRecenterClick}
              iconLeft={<MyLocationIcon className={styles.btnIcon} />}
              size="large"
              className={styles.recenterBtn}
              labelId="labels.recenter"
            />,
            this.recenterControlDiv,
          )}
      </GoogleMap>
    );
  }
}

export default injectIntl(withGoogleMap(PopUpInfoWindowGoogleMap));
